'use client'

import { useTenantButtonColor } from '@/components/TenantButtonColorContext'
import { darkenHex, getContrastTextColor, isValidHex } from '@/lib/colorUtils'
import type { SurveyAnswer, SurveyOption, SurveyQuestion } from '@/types/interactiveVideo'
import { CSSProperties, FC, useState } from 'react'
import { isLeadValueValid, LEAD_PLACEHOLDER } from './surveyCue'

export interface SurveySheetProps {
  /** 展開中に表示する設問 */
  question: SurveyQuestion
  /** 必須設問（閉じるボタンを出さない＝強制ポーズ） */
  isForced: boolean
  /** 提示中設問の番号（1始まり） */
  currentIndex: number
  /** 総設問数 */
  total: number
  /** 既存回答（「戻る」で開き直したときの選択状態の復元用）。無ければ未選択で開く */
  initialAnswer?: SurveyAnswer | null
  /** 連続提示の1つ前へ戻れるか（true のときだけヘッダーに戻るボタンを出す） */
  canGoBack?: boolean
  /** 回答を確定する（SINGLE=optionIds 1件 / MULTI=複数 / FREE=freeText） */
  onSubmit: (answer: SurveyAnswer) => void
  /** シートを閉じる（任意設問のみ） */
  onClose: () => void
  /** 1つ前の設問へ戻る（canGoBack が true のときだけ呼ばれる） */
  onBack?: () => void
}

/** テナントカラー未設定時のヘッダー色（moovy ブルー） */
const DEFAULT_SURVEY_BRAND = '#2563EB'

/**
 * 視聴プレイヤーのアンケート回答シート（Pattern D）。
 *
 * 動画ラッパー下部に下から迫り出すパネルとして描画する。SP/PC 同機構で、
 * 展開時は上部に動画が縮小表示される（レイアウトは親が制御）。
 * 必須設問は閉じるボタンを出さず、回答するまで再生を再開しない。
 *
 * 見た目はブランドヘッダー型。設問はテナントカラーの面に白抜きで置き、回答は
 * その下の淡いグレー面に白いカードで並べる。「読む場所」と「押す場所」が色面で
 * 分かれるので、設問と選択肢を取り違えない。
 * ヘッダー色はテナントの `defaultButtonColor`（CTAボタンと同じ色）を使い、
 * 明るい色を設定されても読めるよう文字色は明度から自動判定する（colorUtils）。
 *
 * 設問型により入力UIを切り替える:
 * - リード（PII）項目（leadFieldKind 非null）: PII 入力欄（EMAIL は形式検証あり）。値はリード表へ。
 * - SINGLE: 選択肢ボタン（タップで即確定）。
 * - MULTI: チェックボックス（複数選択）＋「回答する」で確定。
 * - FREE: 自由記述テキスト＋「回答する」で確定。
 *
 * リード項目は questionType を無視し、選択肢/自由記述の代わりに PII 入力欄のみを出す（ADR-0012）。
 * MULTI/FREE の選択状態はローカルに持つ。設問が変わると親が `key` で再マウントし状態はリセットされる。
 * リード項目の入力欄プレースホルダ（LEAD_PLACEHOLDER）は SurveyGate と共有する（surveyCue・#264）。
 */
const SurveySheet: FC<SurveySheetProps> = ({
  question,
  isForced,
  currentIndex,
  total,
  initialAnswer = null,
  canGoBack = false,
  onSubmit,
  onClose,
  onBack,
}) => {
  // 設問が変わると親が key で再マウントするので、初期値でそのまま復元できる
  const [selectedIds, setSelectedIds] = useState<string[]>(() => initialAnswer?.optionIds ?? [])
  const [freeText, setFreeText] = useState(() => initialAnswer?.freeText ?? '')
  const [leadValue, setLeadValue] = useState(() => initialAnswer?.leadValue ?? '')

  const toggleOption = (option: SurveyOption) => {
    setSelectedIds((prev) => (prev.includes(option.id) ? prev.filter((id) => id !== option.id) : [...prev, option.id]))
  }

  const trimmedFree = freeText.trim()
  const trimmedLead = leadValue.trim()
  const leadFieldKind = question.leadFieldKind
  // リード（PII）項目はメール等の値をリード表へ収集する。questionType より優先して入力欄を出す。
  const isLead = leadFieldKind !== null
  const isMulti = question.questionType === 'MULTI'
  // リード値の有効性判定は SurveyGate と共有（EMAIL は形式検証を継承・他の固定型/TEXT は非空）。
  const isLeadValid = leadFieldKind !== null && isLeadValueValid(leadFieldKind, leadValue)
  const isFreeReady = trimmedFree.length > 0
  const canSubmit = isLead ? isLeadValid : isMulti ? selectedIds.length > 0 : isFreeReady

  // リード項目は PII 値のみを送る（回答表には載らない）。MULTI/FREE は「回答する」で確定。
  const handleSubmit = () =>
    isLead
      ? onSubmit({ optionIds: [], freeText: null, leadValue: trimmedLead })
      : isMulti
        ? onSubmit({ optionIds: selectedIds, freeText: null })
        : onSubmit({ optionIds: [], freeText: trimmedFree })

  // ヘッダー・選択時の色はテナントの CTA ボタン色に合わせる（未設定・不正値なら既定のブルー）。
  // 色は実行時にしか決まらないので CSS カスタムプロパティとして流し込み、
  // 見た目の指定そのものは下の <style> のクラスに寄せる。
  const tenantColor = useTenantButtonColor()
  const brand = tenantColor !== null && isValidHex(tenantColor) ? tenantColor : DEFAULT_SURVEY_BRAND
  const heroForeground = getContrastTextColor(brand)
  const onLightHero = heroForeground === '#000000'
  const sheetVars = {
    '--ms-brand': brand,
    '--ms-brand-deep': darkenHex(brand, 0.28),
    '--ms-fg': heroForeground,
    '--ms-soft': onLightHero ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.24)',
    '--ms-muted': onLightHero ? 'rgba(0, 0, 0, 0.64)' : 'rgba(255, 255, 255, 0.84)',
  } as CSSProperties

  return (
    <div
      data-testid="survey-sheet"
      style={sheetVars}
      className="moovy-survey-sheet pointer-events-auto flex h-full flex-col overflow-hidden rounded-t-2xl bg-[#f7f8fb] shadow-[0_-8px_24px_rgba(0,0,0,0.18)]"
    >
      <style>{`
        @keyframes moovySurveySlideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .moovy-survey-sheet { animation: moovySurveySlideUp 300ms ease-out; }

        /* ブランドヘッダー: テナントカラーの面に設問を白抜きで置く */
        .moovy-survey-hero {
          background-image: linear-gradient(135deg, var(--ms-brand) 0%, var(--ms-brand-deep) 100%);
          color: var(--ms-fg);
        }
        .moovy-survey-progress { color: var(--ms-muted); }
        .moovy-survey-dot { background: var(--ms-soft); }
        .moovy-survey-dot[data-on="true"] { background: var(--ms-fg); }
        .moovy-survey-req { background: var(--ms-soft); color: var(--ms-fg); }
        .moovy-survey-close { color: var(--ms-muted); }
        .moovy-survey-close:hover { background: var(--ms-soft); color: var(--ms-fg); }

        /* 回答カード: 白い面を浮かせて「押せるもの」に見せる */
        .moovy-survey-opt {
          box-shadow: 0 2px 6px rgba(17, 24, 39, 0.07);
          transition: box-shadow 160ms ease, transform 160ms ease, color 160ms ease;
        }
        .moovy-survey-opt:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(17, 24, 39, 0.14);
        }
        .moovy-survey-opt:hover .moovy-survey-tick { background: var(--ms-brand); color: #fff; }
        .moovy-survey-opt[data-on="true"] {
          color: var(--ms-brand-deep);
          font-weight: 700;
          box-shadow: inset 0 0 0 2px var(--ms-brand), 0 6px 16px rgba(17, 24, 39, 0.14);
        }
        .moovy-survey-opt[data-on="true"] .moovy-survey-tick { background: var(--ms-brand); color: #fff; }
        .moovy-survey-tick {
          background: #eef1f6;
          color: transparent;
          transition: background-color 160ms ease, color 160ms ease;
        }

        .moovy-survey-field { box-shadow: 0 2px 6px rgba(17, 24, 39, 0.07); }
        .moovy-survey-field:focus { border-color: var(--ms-brand); }

        .moovy-survey-submit { background: var(--ms-brand); transition: filter 160ms ease; }
        .moovy-survey-submit:not(:disabled):hover { filter: brightness(1.08); }
        .moovy-survey-submit:disabled { background: #d1d5db; cursor: not-allowed; }
      `}</style>

      {/* ブランドヘッダー: 進捗ドット ＋ 閉じる ＋ 設問文 */}
      <div className="moovy-survey-hero flex-shrink-0 px-6 pb-4 pt-3">
        <div className="mb-1.5 flex items-center justify-between gap-3">
          <div className="moovy-survey-progress flex items-center gap-1.5 text-[11.5px] font-bold tracking-wide">
            {/* 連続提示中だけ出す「前の設問へ戻る」。選び直すとサーバ側で上書き保存される */}
            {canGoBack && (
              <button
                data-testid="survey-back"
                aria-label="前の設問へ戻る"
                onClick={onBack}
                className="moovy-survey-close mr-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            {Array.from({ length: total }, (_, i) => (
              <span
                key={i}
                aria-hidden="true"
                data-on={i < currentIndex}
                className="moovy-survey-dot h-[7px] w-[7px] rounded-full"
              />
            ))}
            <span className="ml-1">
              アンケート {currentIndex}/{total}
            </span>
          </div>
          {!isForced && (
            <button
              data-testid="survey-close"
              aria-label="閉じる"
              onClick={onClose}
              className="moovy-survey-close flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* 必須バッジは設問文の直後にインラインで置く（改行しても文末に続く）。
            行送りに載せると設問との対応が離れるため、独立ブロックにはしない。 */}
        <p className="moovy-survey-question text-[16px] font-bold leading-relaxed">
          {question.text}
          {question.required && (
            <span className="moovy-survey-req ml-2 inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 align-middle text-[10.5px] font-bold">
              必須
            </span>
          )}
        </p>
      </div>

      {/* 回答エリア（スクロール可能） */}
      <div className="flex-1 overflow-y-auto px-5 pb-4 pt-4">
        {!isLead && question.questionType === 'SINGLE' && (
          <div className="flex flex-col gap-2.5">
            {question.options.map((option) => (
              <button
                key={option.id}
                data-testid="survey-option"
                aria-pressed={selectedIds.includes(option.id)}
                data-on={selectedIds.includes(option.id)}
                onClick={() => onSubmit({ optionIds: [option.id], freeText: null })}
                className="moovy-survey-opt flex w-full items-center justify-between gap-3 rounded-[13px] bg-white px-[18px] py-4 text-left text-[14px] font-bold text-gray-800"
              >
                <span>{option.label}</span>
                <span className="moovy-survey-tick flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full">
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              </button>
            ))}
          </div>
        )}

        {!isLead && isMulti && (
          <div className="flex flex-col gap-2.5">
            {question.options.map((option) => {
              const checked = selectedIds.includes(option.id)
              return (
                <button
                  key={option.id}
                  data-testid="survey-option"
                  aria-pressed={checked}
                  data-on={checked}
                  onClick={() => toggleOption(option)}
                  className="moovy-survey-opt flex w-full items-center justify-between gap-3 rounded-[13px] bg-white px-[18px] py-4 text-left text-[14px] font-bold text-gray-800"
                >
                  <span>{option.label}</span>
                  <span className="moovy-survey-tick flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-[7px]">
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {!isLead && question.questionType === 'FREE' && (
          <textarea
            data-testid="survey-free-input"
            value={freeText}
            onChange={(event) => setFreeText(event.target.value)}
            rows={4}
            placeholder="ご自由にご記入ください"
            className="survey-free-answer moovy-survey-field w-full resize-none rounded-[13px] border border-[#e3e7ee] bg-white px-4 py-3.5 text-[16px] text-gray-800 outline-none transition-colors placeholder:text-[#4b5563] placeholder:opacity-100"
          />
        )}

        {/* リード（PII）項目: 入力欄を出し、値を回答とは別のリード表へ隔離保存する（ADR-0012）。 */}
        {leadFieldKind !== null && (
          <input
            type={leadFieldKind === 'EMAIL' ? 'email' : 'text'}
            inputMode={leadFieldKind === 'EMAIL' ? 'email' : undefined}
            autoComplete={leadFieldKind === 'EMAIL' ? 'email' : undefined}
            data-testid="survey-lead-input"
            value={leadValue}
            onChange={(event) => setLeadValue(event.target.value)}
            placeholder={LEAD_PLACEHOLDER[leadFieldKind]}
            className="moovy-survey-field w-full rounded-[13px] border border-[#e3e7ee] bg-white px-4 py-3.5 text-[16px] text-gray-800 outline-none transition-colors placeholder:text-[#4b5563] placeholder:opacity-100"
          />
        )}

        {/* リード項目・MULTI・FREE の確定ボタン（SINGLE は選択肢タップで即確定するため無し） */}
        {(isLead || question.questionType !== 'SINGLE') && (
          <button
            data-testid="survey-submit"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="moovy-survey-submit mt-4 w-full rounded-xl px-4 py-3.5 text-[16px] font-bold text-white"
          >
            回答する
          </button>
        )}
      </div>
    </div>
  )
}

export default SurveySheet
